import { useEffect } from "react";
import { styled } from "@glitz/react";
import { spacing, green, darkGrey, fontWeight, fontSize, grey, black } from "../theme";
import {
  useShipping,
  SHIPPING_METHODS,
  FREE_SHIPPING_THRESHOLD,
  getShippingPrice,
  isShippingClassLight,
  ShippingMethodId,
} from "../context/ShippingContext";
import { useCart } from "../context/CartContext";

const ShippingMethodSelector = () => {
  const { shippingMethod, setShippingMethod } = useShipping();
  const { cart, getMerchandiseAfterPackage } = useCart();
  const totalPrice = getMerchandiseAfterPackage();
  const hasFreeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;

  const isItemInStock = (item: { in_stock?: boolean; stock_status?: string }) =>
    item.in_stock === true || item.stock_status === "instock";
  const allItemsInStock =
    cart.length > 0 && cart.every(isItemInStock);

  const allItemsShippingLight =
    cart.length > 0 &&
    cart.every((item) => isShippingClassLight(item.shipping_class));

  useEffect(() => {
    if (!allItemsInStock) {
      if (shippingMethod === "express") setShippingMethod("standard");
      if (shippingMethod === "express_light") setShippingMethod("standard_light");
    }
  }, [shippingMethod, allItemsInStock, setShippingMethod]);

  useEffect(() => {
    if (allItemsShippingLight && (shippingMethod === "standard" || shippingMethod === "express")) {
      setShippingMethod("standard_light");
    } else if (!allItemsShippingLight && (shippingMethod === "standard_light" || shippingMethod === "express_light")) {
      setShippingMethod("standard");
    }
  }, [allItemsShippingLight, shippingMethod, setShippingMethod]);

  const visibleMethods = SHIPPING_METHODS.filter((m) => {
    if (m.id === "standard") return !allItemsShippingLight;
    if (m.id === "standard_light") return allItemsShippingLight;
    if (m.id === "express") return !allItemsShippingLight;
    if (m.id === "express_light") return allItemsShippingLight;
    return true;
  });

  return (
    <Wrapper>
      <Header>Välj leveranssätt</Header>
      <OptionsWrapper>
        {visibleMethods.map((method) => {
          const price = getShippingPrice(method.id, totalPrice);
          const isSelected = shippingMethod === method.id;
          const originalPrice = method.price;
          const isDiscounted = hasFreeShipping && originalPrice > 0 && price < originalPrice;
          const isExpressDisabled =
            (method.id === "express" || method.id === "express_light") && !allItemsInStock;

          return (
            <OptionWrapper key={method.id}>
              <Option
                onClick={() =>
                  !isExpressDisabled && setShippingMethod(method.id as ShippingMethodId)
                }
                style={{
                  ...(isSelected ? { borderColor: green } : {}),
                  ...(isExpressDisabled
                    ? {
                        opacity: 0.7,
                        cursor: "not-allowed",
                        pointerEvents: "none",
                      }
                    : {}),
                }}
              >
                <RadioOuter
                  style={
                    isExpressDisabled ? { borderColor: grey } : undefined
                  }
                >
                  {isSelected && <RadioInner />}
                </RadioOuter>
                <OptionContent>
                  <OptionLabel
                    style={isExpressDisabled ? { color: darkGrey } : undefined}
                  >
                    {method.label}
                  </OptionLabel>
                  <PriceWrapper>
                    {isDiscounted && <OldPrice>{originalPrice} kr</OldPrice>}
                    <OptionPrice
                      style={
                        isExpressDisabled ? { color: darkGrey } : undefined
                      }
                    >
                      {`${price} kr`}
                    </OptionPrice>
                  </PriceWrapper>
                </OptionContent>
              </Option>
              {isExpressDisabled && (
                <ExpressDisabledNote>
                  Kräver att alla varor i varukorgen finns i lager
                </ExpressDisabledNote>
              )}
            </OptionWrapper>
          );
        })}
      </OptionsWrapper>
      {hasFreeShipping && (
        <FreeShippingNote>
          Du har nått fri frakt-gränsen på {FREE_SHIPPING_THRESHOLD} kr!
        </FreeShippingNote>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "50%",
  justifySelf: "center",
  gap: spacing.m,
});

const Header = styled.h2({
  textAlign: "center",
  margin: { bottom: 0 },
});

const OptionsWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const OptionWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const Option = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.m,
  padding: { xy: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "2px" } },
  borderRadius: "8px",
  cursor: "pointer",
  transition: { property: "border-color", duration: "0.15s", timingFunction: "ease" },
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "2px" } },
  },
});

const RadioOuter = styled.div({
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  border: { xy: { style: "solid", color: darkGrey, width: "2px" } },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const RadioInner = styled.div({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor: green,
});

const OptionContent = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const OptionLabel = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
});

const PriceWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const OldPrice = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
  textDecoration: "line-through",
});

const OptionPrice = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
});

const ExpressDisabledNote = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  padding: { left: spacing.m },
});

const FreeShippingNote = styled.div({
  textAlign: "center",
  fontSize: fontSize.s,
  color: green,
  fontWeight: fontWeight.bold,
});

export default ShippingMethodSelector;
